import React, { useState, useEffect } from "react";
import { Check, X, Thermometer, BarChart, Car } from "lucide-react";
import ModuleNavigation from "../components/ModuleNavigation";

const CarbonEmissions101 = () => {
  const [activeSection, setActiveSection] = useState(1);
  const [completedSections, setCompletedSections] = useState({});
  const [timelineMatches, setTimelineMatches] = useState({});
  const [resultsSaved, setResultsSaved] = useState(false);
  const [personalFootprint, setPersonalFootprint] = useState({
    breakfast: { item: "", amount: "", footprint: 0 },
    transport: { item: "", amount: "", footprint: 0 },
    energy: { item: "", amount: "", footprint: 0 },
    other: { item: "", amount: "", footprint: 0 },
  });

  // Add this function to check if all milestone items have been matched
  const checkAllMatched = () => {
    const dropZones = document.querySelectorAll("[data-target]");
    let allMatched = true;

    dropZones.forEach((zone) => {
      if (!zone.innerHTML.includes("svg")) {
        allMatched = false;
      }
    });

    const feedbackElement = document.getElementById("match-feedback");
    if (feedbackElement && allMatched) {
      feedbackElement.classList.remove("hidden");
    }
  };

  // Add shake animation style
  useEffect(() => {
    if (!document.getElementById("shake-animation")) {
      const style = document.createElement("style");
      style.id = "shake-animation";
      style.innerHTML = `
        @keyframes shake {
          0% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          50% { transform: translateX(5px); }
          75% { transform: translateX(-5px); }
          100% { transform: translateX(0); }
        }
        .animate-shake {
          animation: shake 0.5s;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  const handleSectionComplete = (sectionNumber) => {
    setCompletedSections({ ...completedSections, [sectionNumber]: true });

    // Check if this completes the module
    if (sectionNumber === 7) {
      document.getElementById("module-complete-button")?.click();
    }
  };

  const handleModuleComplete = () => {
    // Mark all sections as complete
    const allComplete = {};
    for (let i = 1; i <= 7; i++) {
      allComplete[i] = true;
    }
    setCompletedSections(allComplete);

    // Show saved confirmation
    setResultsSaved(true);
  };

  const handleTimelineMatch = (milestone, trigger) => {
    setTimelineMatches({ ...timelineMatches, [milestone]: trigger });
    if (Object.keys(timelineMatches).length >= 2) {
      handleSectionComplete(1);
    }
  };

  const handleFootprintCalculation = (category, item, amount) => {
    // Simplified emission factors for demo
    const emissionFactors = {
      coffee: 0.5, // kg CO2e per cup
      eggs: 0.3, // kg CO2e per egg
      toast: 0.1, // kg CO2e per slice
      car: 0.25, // kg CO2e per km
      bus: 0.1, // kg CO2e per km
      electricity: 0.42, // kg CO2e per kWh
      gas: 0.2, // kg CO2e per kWh
    };

    const footprint = amount * (emissionFactors[item] || 0);

    setPersonalFootprint({
      ...personalFootprint,
      [category]: { item, amount, footprint },
    });
  };

  // Fix drag/drop handlers to safely check for elements
  const handleDragStart = (e, id) => {
    if (e && e.dataTransfer) {
      e.dataTransfer.setData("text/plain", id);
      e.target.classList.add("opacity-50");
    }
  };

  const handleDragEnd = (e) => {
    if (e && e.target) {
      e.target.classList.remove("opacity-50");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-10">
        <h1 className="text-4xl font-bold text-primary-600 mb-2">
          Carbon Emissions 101<span className="text-edu-orange">.</span>
        </h1>
        <div className="w-24 h-1 bg-edu-orange mx-auto mt-2 mb-6"></div>
      </div>

      {/* Module Banner */}
      <div className="bg-primary-600 p-8 rounded-lg mb-12">
        <h2 className="text-2xl font-bold mb-4">
          From History to Your Personal Numbers
        </h2>
        <p className="text-lg mb-6">
          By the end of this lesson you'll be able to:
        </p>
        <ul className="space-y-4 mb-6">
          <li className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5 text-primary-600">
              <span className="text-sm">1</span>
            </div>
            <p>
              Understand why carbon-footprint tracking emerged and its
              significance
            </p>
          </li>
          <li className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5 text-primary-600">
              <span className="text-sm">2</span>
            </div>
            <p>
              Calculate carbon footprints using the basic formula and reliable
              sources
            </p>
          </li>
          <li className="flex items-start">
            <div className="flex-shrink-0 h-6 w-6 rounded-full bg-white flex items-center justify-center mr-3 mt-0.5 text-primary-600">
              <span className="text-sm">3</span>
            </div>
            <p>
              Compare your personal emissions to meaningful real-world
              benchmarks
            </p>
          </li>
        </ul>
        <p className="text-sm font-medium border-t border-white pt-4">
          Average seat-time: ≈ 25 minutes.
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap border-b border-gray-200 mb-8 overflow-x-auto relative">
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeSection === 1
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(1)}
        >
          1. Carbon Footprint History
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeSection === 2
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(2)}
        >
          2. Emissions Scale
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeSection === 3
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(3)}
        >
          3. Footprint Formula
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeSection === 4
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(4)}
        >
          4. Emission Factors
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeSection === 5
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(5)}
        >
          5. Calculation Limits
        </button>
        <button
          className={`px-4 py-2 mr-2 whitespace-nowrap ${
            activeSection === 6
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(6)}
        >
          6. Why Counting Matters
        </button>
        <button
          className={`px-4 py-2 whitespace-nowrap ${
            activeSection === 7
              ? "text-primary-500 border-b-2 border-primary-500 font-medium"
              : "text-gray-600 hover:text-primary-500"
          }`}
          onClick={() => setActiveSection(7)}
        >
          7. Personal Footprint
        </button>
      </div>

      {/* Tab Content */}
      <div className="mb-16">
        {activeSection === 1 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Why Did We Start Counting Carbon?
            </h2>

            {/* Hook Animation Section - REPLACING WITH BETTER DESIGN */}
            <div className="relative h-32 mb-8 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg overflow-hidden">
              <div className="absolute inset-0 flex items-center px-8">
                <div className="w-full">
                  <p className="text-xl italic font-medium text-gray-800">
                    "We didn't always care how much CO<sub>2</sub> a latte
                    costs—so how did carbon math become your problem?"
                  </p>
                </div>
              </div>
            </div>

            {/* Timeline "Speed-Run" */}
            <div className="mb-10">
              <h3 className="text-lg font-medium text-gray-800 mb-4">
                Carbon Footprint Timeline
              </h3>

              <div className="relative mb-6">
                {/* Timeline Progress Bar */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 rounded">
                  <div
                    className="absolute top-0 left-0 h-full bg-primary-600 rounded"
                    style={{ width: "100%" }}
                  ></div>
                </div>
              </div>

              <div className="space-y-6">
                {/* 1750 Timeline Item - FIX TEXT COLORS */}
                <div className="flex shadow-sm hover:shadow-md transition-shadow rounded-lg overflow-hidden">
                  <div className="w-24 bg-primary-700 text-white flex flex-col items-center justify-center font-bold">
                    <div className="text-2xl">1750</div>
                    <div className="text-xs">BASELINE</div>
                  </div>
                  <div className="flex-grow p-4 bg-primary-50">
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                        <svg
                          className="w-6 h-6 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800">
                          Pre-industrial Balance
                        </h4>
                        <p className="text-gray-700">
                          Before engines, Earth's books balanced at 280 parts
                          per million.
                        </p>
                        <div className="mt-1 text-xs text-primary-700 font-medium">
                          CO₂: 280 ppm | Emissions: 0.3 Gt/yr (mostly land-use)
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 1958 Timeline Item */}
                <div className="flex shadow-sm hover:shadow-md transition-shadow rounded-lg overflow-hidden">
                  <div className="w-24 bg-primary-500 text-white flex flex-col items-center justify-center font-bold">
                    <div className="text-2xl">1958</div>
                    <div className="text-xs">MEASUREMENT</div>
                  </div>
                  <div className="flex-grow p-4 bg-primary-50">
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mr-4">
                        <svg
                          className="w-6 h-6 text-blue-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800">
                          Keeling Curve Begins
                        </h4>
                        <p className="text-gray-700">
                          Dave Keeling starts daily CO₂ readings at Mauna
                          Loa—the first evidence of steady rise.
                        </p>
                        <div className="mt-1 text-xs text-primary-700 font-medium">
                          CO₂: 315 ppm | Emissions: ~6 Gt/yr | Trigger:{" "}
                          <span className="bg-blue-100 px-1 rounded text-blue-800">
                            Technology
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 1992 Timeline Item */}
                <div className="flex shadow-sm hover:shadow-md transition-shadow rounded-lg overflow-hidden">
                  <div className="w-24 bg-primary-700 text-white flex flex-col items-center justify-center font-bold">
                    <div className="text-2xl">1992</div>
                    <div className="text-xs text-center">GLOBAL ACTION</div>
                  </div>
                  <div className="flex-grow p-4 bg-primary-50">
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mr-4">
                        <svg
                          className="w-6 h-6 text-green-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800">
                          Rio Earth Summit
                        </h4>
                        <p className="text-gray-700">
                          World leaders gather in Rio; "Ecological Footprint"
                          concept is coined the same year.
                        </p>
                        <div className="mt-1 text-xs text-primary-700 font-medium">
                          CO₂: 356 ppm | Emissions: 24 Gt/yr | Trigger:{" "}
                          <span className="bg-green-100 px-1 rounded text-green-800">
                            Policy
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2004 Timeline Item */}
                <div className="flex shadow-sm hover:shadow-md transition-shadow rounded-lg overflow-hidden">
                  <div className="w-24 bg-primary-500 text-white flex flex-col items-center justify-center font-bold">
                    <div className="text-2xl">2004</div>
                    <div className="text-xs">PERSONAL</div>
                  </div>
                  <div className="flex-grow p-4 bg-primary-50">
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mr-4">
                        <svg
                          className="w-6 h-6 text-amber-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800">
                          BP Carbon Calculator Launch
                        </h4>
                        <p className="text-gray-700">
                          BP launches first widely-used carbon
                          calculator—critics note the shift from industry to
                          individual.
                        </p>
                        <div className="mt-1 text-xs text-primary-700 font-medium">
                          CO₂: 377 ppm | Emissions: 38 Gt/yr | Trigger:{" "}
                          <span className="bg-amber-100 px-1 rounded text-amber-800">
                            Corporate
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 2023 Timeline Item */}
                <div className="flex shadow-sm hover:shadow-md transition-shadow rounded-lg overflow-hidden">
                  <div className="w-24 bg-primary-700 text-white flex flex-col items-center justify-center font-bold">
                    <div className="text-2xl">2023</div>
                    <div className="text-xs">TODAY</div>
                  </div>
                  <div className="flex-grow p-4 bg-primary-50">
                    <div className="flex items-center">
                      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mr-4">
                        <svg
                          className="w-6 h-6 text-purple-600"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                          ></path>
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-800">
                          Carbon Tracking Goes Digital
                        </h4>
                        <p className="text-gray-700">
                          Real-time footprint apps put the tally in your palm
                          with unprecedented precision.
                        </p>
                        <div className="mt-1 text-xs text-primary-700 font-medium">
                          CO₂: 420+ ppm | Emissions: 53 Gt/yr | Trigger:{" "}
                          <span className="bg-purple-100 px-1 rounded text-purple-800">
                            Technology
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Activity matching section */}
            <div className="bg-primary-50 p-6 rounded-lg mt-8">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 text-center">
                Activity: Match Milestones to Their Triggers
              </h3>

              <p className="mb-6 text-gray-700 text-center">
                Drag each milestone to match what triggered that phase of the
                carbon counting evolution.
                <span className="block text-sm italic mt-1 text-center">
                  Get all three correct to continue!
                </span>
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h4 className="font-medium mb-3 text-center">Milestones:</h4>
                  <div className="space-y-3" id="draggable-items">
                    <div
                      className="p-3 border rounded-md bg-white cursor-move"
                      draggable="true"
                      data-id="keeling"
                      data-correct="tech"
                      onDragStart={(e) => handleDragStart(e, "keeling")}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="font-medium">Keeling Curve</div>
                      <p className="text-sm text-gray-600 mt-1">
                        First continuous CO₂ measurements
                      </p>
                    </div>

                    <div
                      className="p-3 border rounded-md bg-white cursor-move"
                      draggable="true"
                      data-id="rio"
                      data-correct="policy"
                      onDragStart={(e) => handleDragStart(e, "rio")}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="font-medium">Rio Earth Summit</div>
                      <p className="text-sm text-gray-600 mt-1">
                        First major climate agreement
                      </p>
                    </div>

                    <div
                      className="p-3 border rounded-md bg-white cursor-move"
                      draggable="true"
                      data-id="bp"
                      data-correct="corporate"
                      onDragStart={(e) => handleDragStart(e, "bp")}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="font-medium">BP Calculator Launch</div>
                      <p className="text-sm text-gray-600 mt-1">
                        First major public carbon calculator
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium mb-3 text-center">Triggers:</h4>
                  <div className="space-y-4">
                    <div
                      className="h-24 p-3 border-2 border-dashed rounded-md bg-blue-50 flex items-center justify-center"
                      data-target="tech"
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (e.currentTarget) {
                          e.currentTarget.classList.add(
                            "border-blue-300",
                            "bg-blue-50"
                          );
                        }
                      }}
                      onDragLeave={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.classList.remove(
                            "border-blue-300",
                            "bg-blue-50"
                          );
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (!e.currentTarget || !e.dataTransfer) return;

                        e.currentTarget.classList.remove(
                          "border-blue-300",
                          "bg-blue-50"
                        );

                        const id = e.dataTransfer.getData("text/plain");
                        if (!id) return;

                        const draggedElement = document.querySelector(
                          `[data-id="${id}"]`
                        );
                        if (!draggedElement || !draggedElement.dataset) return;

                        const correct =
                          draggedElement.dataset.correct === "tech";

                        // Handle correct/incorrect
                        if (correct) {
                          const fontMediumElement =
                            draggedElement.querySelector(".font-medium");
                          const innerText = fontMediumElement
                            ? fontMediumElement.innerText
                            : "Keeling Curve";

                          e.currentTarget.innerHTML = `
                            <div class="p-3 bg-green-100 border border-green-300 rounded-md w-full">
                              <div class="flex justify-between items-center">
                                <span class="font-medium">${innerText}</span>
                                <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                </svg>
                              </div>
                            </div>
                          `;

                          if (draggedElement.style) {
                            draggedElement.style.display = "none";
                          }

                          // Update timeline matches
                          handleTimelineMatch(id, "tech");

                          // Check if all matched
                          checkAllMatched();
                        } else {
                          // Shake effect for incorrect match
                          e.currentTarget.classList.add("animate-shake");
                          setTimeout(() => {
                            if (e.currentTarget) {
                              e.currentTarget.classList.remove("animate-shake");
                            }
                          }, 500);
                        }
                      }}
                    >
                      <div>
                        <div className="font-medium text-center text-blue-800">
                          Technology Shift
                        </div>
                        <p className="text-xs text-blue-600 mt-1 text-center">
                          Scientific measurement & data collection
                        </p>
                      </div>
                    </div>

                    <div
                      className="h-24 p-3 border-2 border-dashed rounded-md bg-green-50 flex items-center justify-center"
                      data-target="policy"
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (e.currentTarget) {
                          e.currentTarget.classList.add(
                            "border-green-300",
                            "bg-green-50"
                          );
                        }
                      }}
                      onDragLeave={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.classList.remove(
                            "border-green-300",
                            "bg-green-50"
                          );
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (!e.currentTarget || !e.dataTransfer) return;

                        e.currentTarget.classList.remove(
                          "border-green-300",
                          "bg-green-50"
                        );

                        const id = e.dataTransfer.getData("text/plain");
                        if (!id) return;

                        const draggedElement = document.querySelector(
                          `[data-id="${id}"]`
                        );
                        if (!draggedElement || !draggedElement.dataset) return;

                        const correct =
                          draggedElement.dataset.correct === "policy";

                        // Handle correct/incorrect
                        if (correct) {
                          const fontMediumElement =
                            draggedElement.querySelector(".font-medium");
                          const innerText = fontMediumElement
                            ? fontMediumElement.innerText
                            : "Rio Earth Summit";

                          e.currentTarget.innerHTML = `
                            <div class="p-3 bg-green-100 border border-green-300 rounded-md w-full">
                              <div class="flex justify-between items-center">
                                <span class="font-medium">${innerText}</span>
                                <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                </svg>
                              </div>
                            </div>
                          `;

                          if (draggedElement.style) {
                            draggedElement.style.display = "none";
                          }

                          // Update timeline matches
                          handleTimelineMatch(id, "policy");

                          // Check if all matched
                          checkAllMatched();
                        } else {
                          // Shake effect for incorrect match
                          e.currentTarget.classList.add("animate-shake");
                          setTimeout(() => {
                            if (e.currentTarget) {
                              e.currentTarget.classList.remove("animate-shake");
                            }
                          }, 500);
                        }
                      }}
                    >
                      <div>
                        <div className="font-medium text-center text-green-800">
                          Policy Shift
                        </div>
                        <p className="text-xs text-green-600 mt-1 text-center">
                          Government action & international agreements
                        </p>
                      </div>
                    </div>

                    <div
                      className="h-24 p-3 border-2 border-dashed rounded-md bg-amber-50 flex items-center justify-center"
                      data-target="corporate"
                      onDragOver={(e) => {
                        e.preventDefault();
                        if (e.currentTarget) {
                          e.currentTarget.classList.add(
                            "border-amber-300",
                            "bg-amber-50"
                          );
                        }
                      }}
                      onDragLeave={(e) => {
                        if (e.currentTarget) {
                          e.currentTarget.classList.remove(
                            "border-amber-300",
                            "bg-amber-50"
                          );
                        }
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        if (!e.currentTarget || !e.dataTransfer) return;

                        e.currentTarget.classList.remove(
                          "border-amber-300",
                          "bg-amber-50"
                        );

                        const id = e.dataTransfer.getData("text/plain");
                        if (!id) return;

                        const draggedElement = document.querySelector(
                          `[data-id="${id}"]`
                        );
                        if (!draggedElement || !draggedElement.dataset) return;

                        const correct =
                          draggedElement.dataset.correct === "corporate";

                        // Handle correct/incorrect
                        if (correct) {
                          const fontMediumElement =
                            draggedElement.querySelector(".font-medium");
                          const innerText = fontMediumElement
                            ? fontMediumElement.innerText
                            : "BP Calculator Launch";

                          e.currentTarget.innerHTML = `
                            <div class="p-3 bg-green-100 border border-green-300 rounded-md w-full">
                              <div class="flex justify-between items-center">
                                <span class="font-medium">${innerText}</span>
                                <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                                </svg>
                              </div>
                            </div>
                          `;

                          if (draggedElement.style) {
                            draggedElement.style.display = "none";
                          }

                          // Update timeline matches
                          handleTimelineMatch(id, "corporate");

                          // Check if all matched
                          checkAllMatched();
                        } else {
                          // Shake effect for incorrect match
                          e.currentTarget.classList.add("animate-shake");
                          setTimeout(() => {
                            if (e.currentTarget) {
                              e.currentTarget.classList.remove("animate-shake");
                            }
                          }, 500);
                        }
                      }}
                    >
                      <div>
                        <div className="font-medium text-center text-amber-800">
                          Corporate/Marketing Shift
                        </div>
                        <p className="text-xs text-amber-600 mt-1 text-center">
                          Individual responsibility & corporate messaging
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                id="match-feedback"
                className="hidden mt-6 bg-green-50 border border-green-200 rounded-md p-4"
              >
                <div className="flex">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <p className="font-medium text-green-800">
                    Correct! You've successfully matched each milestone to its
                    driver.
                  </p>
                </div>
                <p className="mt-2 text-green-700">
                  Carbon tracking evolved from scientific measurement to
                  international policy to corporate-driven personal
                  responsibility.
                </p>
                <div className="mt-4">
                  <button
                    id="continue-button"
                    onClick={() => {
                      handleSectionComplete(1);
                      setActiveSection(2);
                    }}
                    className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                  >
                    Continue to Emissions Scale
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeSection === 2 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Pre- vs. Post-Industrial Scale
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              <div className="lg:col-span-2">
                <p className="text-gray-700 mb-4">
                  Earth's atmospheric CO₂ remained relatively stable at around
                  280 ppm for thousands of years before the industrial
                  revolution. Since then, human activities have increased
                  emissions by a factor of 175 - from 0.3 Gt CO₂e/year to over
                  53 Gt CO₂e/year today.
                </p>
                <p className="text-gray-700 mb-4">
                  This dramatic increase reflects our growing reliance on fossil
                  fuels, intensive agriculture, and deforestation. The impact
                  isn't just global - it's also personal. The average person
                  today is responsible for 4-7 tonnes of CO₂e annually - more
                  than 10,000 times what a pre-industrial person would have
                  emitted.
                </p>

                <div className="bg-amber-50 p-4 mt-6 rounded-md">
                  <div className="flex mb-1">
                    <Thermometer className="text-amber-700 h-5 w-5 mr-2" />
                    <h3 className="font-bold text-amber-800">
                      Key Comparisons
                    </h3>
                  </div>
                  <ul className="list-disc ml-6 space-y-1 text-amber-900">
                    <li>
                      Global annual emissions:{" "}
                      <span className="font-bold">175× increase</span> since
                      1750
                    </li>
                    <li>
                      CO₂ concentration: from 280 ppm to 420 ppm (
                      <span className="font-bold">50% increase</span>)
                    </li>
                    <li>
                      Individual carbon footprint:{" "}
                      <span className="font-bold">&gt;10,000× increase</span>{" "}
                      per person
                    </li>
                  </ul>
                </div>
              </div>

              <div className="relative h-auto">
                <img
                  src="/images/figure1-module3.webp"
                  alt="Pre-industrial vs today carbon emissions comparison"
                  className="w-full h-auto rounded-lg"
                />
              </div>
            </div>

            <div className="flex justify-end mt-8">
              <button
                onClick={() => {
                  handleSectionComplete(2);
                  setActiveSection(3);
                }}
                className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
              >
                Continue to Footprint Formula
              </button>
            </div>
          </div>
        )}

        {activeSection === 3 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              The Footprint Formula
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              <div className="lg:col-span-2">
                <p className="text-gray-700 mb-4">
                  Carbon footprints are calculated using a simple formula that
                  multiplies your activity data by the appropriate emission
                  factor. This transforms everyday activities into their climate
                  impact, measured in kilograms of carbon dioxide equivalent (kg
                  CO₂e).
                </p>

                <div className="bg-primary-50 p-4 rounded-md mb-6 text-center">
                  <p className="text-lg font-bold text-primary-800">
                    Carbon Footprint (kg CO₂e) = Activity Data × Emission Factor
                  </p>
                </div>

                <table className="min-w-full mb-6">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-3 text-left">Variable</th>
                      <th className="p-3 text-left">Example inputs</th>
                      <th className="p-3 text-left">Where to find it</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-3 font-medium">Activity data</td>
                      <td className="p-3">
                        15 km commute, 1 kWh electricity, 250 g beef
                      </td>
                      <td className="p-3">Your bills, receipts, fitness app</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Emission factor</td>
                      <td className="p-3">
                        0.25 kg CO₂e/km (average petrol car), 0.42 kg CO₂e/kWh
                        (UAE grid), 27 kg CO₂e/kg beef (FAO)
                      </td>
                      <td className="p-3">Databases (next section)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-primary-100 p-4 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <BarChart className="h-12 w-12 mx-auto text-primary-600 mb-4" />
                  <h3 className="text-lg font-medium text-primary-700 mb-1">
                    Example
                  </h3>
                  <div className="space-y-2 text-primary-800">
                    <p>
                      10 km car trip × 0.25 kg CO₂e/km ={" "}
                      <span className="font-bold">2.5 kg CO₂e</span>
                    </p>
                    <p>
                      300g beef × 27 kg CO₂e/kg ={" "}
                      <span className="font-bold">8.1 kg CO₂e</span>
                    </p>
                    <p>
                      1 hour AC × 2 kWh × 0.42 kg CO₂e/kWh ={" "}
                      <span className="font-bold">0.84 kg CO₂e</span>
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Interactive Calculator: Your Breakfast Footprint
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <div className="space-y-4 mb-4">
                    <div>
                      <label className="block mb-1 text-sm font-medium">
                        Food item 1
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          className="p-2 border rounded w-full"
                          onChange={(e) =>
                            handleFootprintCalculation(
                              "breakfast",
                              e.target.value,
                              personalFootprint.breakfast.amount || 1
                            )
                          }
                        >
                          <option value="">Select food</option>
                          <option value="coffee">Coffee</option>
                          <option value="eggs">Eggs</option>
                          <option value="toast">Toast</option>
                          <option value="beef">Beef sausage</option>
                          <option value="milk">Milk (200ml)</option>
                          <option value="cereal">Cereal (50g)</option>
                          <option value="fruit">Fruit (apple/banana)</option>
                        </select>

                        <div className="flex">
                          <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md">
                            Qty
                          </span>
                          <input
                            type="number"
                            min="1"
                            className="p-2 border rounded-r-md w-full"
                            value={personalFootprint.breakfast.amount || ""}
                            onChange={(e) =>
                              handleFootprintCalculation(
                                "breakfast",
                                personalFootprint.breakfast.item,
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block mb-1 text-sm font-medium">
                        Food item 2
                      </label>
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          className="p-2 border rounded w-full"
                          onChange={(e) =>
                            handleFootprintCalculation(
                              "other",
                              e.target.value,
                              personalFootprint.other.amount || 1
                            )
                          }
                        >
                          <option value="">Select food</option>
                          <option value="coffee">Coffee</option>
                          <option value="eggs">Eggs</option>
                          <option value="toast">Toast</option>
                          <option value="beef">Beef sausage</option>
                          <option value="milk">Milk (200ml)</option>
                          <option value="cereal">Cereal (50g)</option>
                          <option value="fruit">Fruit (apple/banana)</option>
                        </select>

                        <div className="flex">
                          <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md">
                            Qty
                          </span>
                          <input
                            type="number"
                            min="1"
                            className="p-2 border rounded-r-md w-full"
                            value={personalFootprint.other.amount || ""}
                            onChange={(e) =>
                              handleFootprintCalculation(
                                "other",
                                personalFootprint.other.item,
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-500 italic">
                    *Based on average emission factors from multiple sources.
                    Actual values may vary.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 border">
                  <h4 className="font-medium mb-3 text-center">
                    Breakfast Carbon Footprint
                  </h4>

                  <div className="space-y-3 mb-4">
                    {personalFootprint.breakfast.item && (
                      <div className="flex justify-between border-b pb-2">
                        <span>
                          {personalFootprint.breakfast.amount || 1}×{" "}
                          {personalFootprint.breakfast.item}
                        </span>
                        <span className="font-medium">
                          {personalFootprint.breakfast.footprint.toFixed(2)} kg
                          CO₂e
                        </span>
                      </div>
                    )}

                    {personalFootprint.other.item && (
                      <div className="flex justify-between border-b pb-2">
                        <span>
                          {personalFootprint.other.amount || 1}×{" "}
                          {personalFootprint.other.item}
                        </span>
                        <span className="font-medium">
                          {personalFootprint.other.footprint.toFixed(2)} kg CO₂e
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between pt-2 font-bold">
                      <span>Total</span>
                      <span>
                        {(
                          personalFootprint.breakfast.footprint +
                          personalFootprint.other.footprint
                        ).toFixed(2)}{" "}
                        kg CO₂e
                      </span>
                    </div>
                  </div>

                  {/* Comparison */}
                  {(personalFootprint.breakfast.footprint > 0 ||
                    personalFootprint.other.footprint > 0) && (
                    <div className="mt-4 bg-green-50 p-3 rounded text-sm">
                      <p>
                        <span className="font-medium">Comparison:</span> Your
                        breakfast carbon footprint is equivalent to driving
                        approximately{" "}
                        {(
                          (personalFootprint.breakfast.footprint +
                            personalFootprint.other.footprint) /
                          0.25
                        ).toFixed(1)}{" "}
                        km in an average car.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    handleSectionComplete(3);
                    setActiveSection(4);
                  }}
                  className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                >
                  Continue to Emission Factors
                </button>
              </div>
            </div>
          </div>
        )}

        {activeSection === 4 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-4">
              3.4 Where Do Factors Come From? The "Source Deck"
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="border rounded-md p-4 hover:shadow-md transition-shadow">
                <div className="font-bold mb-2">IPCC default factors</div>
                <p className="text-sm mb-2">Best for: Global fuel/energy</p>
                <p className="text-sm text-gray-600">
                  Example: Coal combustion: 2.86 kg CO₂e/litre
                </p>
                <div className="mt-3">
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    Global
                  </span>
                </div>
              </div>

              <div className="border rounded-md p-4 hover:shadow-md transition-shadow">
                <div className="font-bold mb-2">IEA electricity mix</div>
                <p className="text-sm mb-2">Best for: Country-grid kWh</p>
                <p className="text-sm text-gray-600">
                  Example: UAE 0.42 kg CO₂e/kWh (2023 est.)
                </p>
                <div className="mt-3">
                  <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                    Regional
                  </span>
                </div>
              </div>

              <div className="border rounded-md p-4 hover:shadow-md transition-shadow">
                <div className="font-bold mb-2">FAO FoodStats</div>
                <p className="text-sm mb-2">Best for: Agriculture & diet</p>
                <p className="text-sm text-gray-600">
                  Example: Beef 27 kg CO₂e/kg; Lentils 0.9
                </p>
                <div className="mt-3">
                  <span className="inline-block bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded">
                    Sectoral
                  </span>
                </div>
              </div>

              <div className="border rounded-md p-4 hover:shadow-md transition-shadow">
                <div className="font-bold mb-2">US EPA WARM</div>
                <p className="text-sm mb-2">Best for: Waste & small items</p>
                <p className="text-sm text-gray-600">
                  Example: Landfilled plastic bottle 0.06 kg
                </p>
                <div className="mt-3">
                  <span className="inline-block bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                    Sectoral
                  </span>
                </div>
              </div>

              <div className="border rounded-md p-4 hover:shadow-md transition-shadow">
                <div className="font-bold mb-2">UK DEFRA</div>
                <p className="text-sm mb-2">Best for: Flights, apparel</p>
                <p className="text-sm text-gray-600">
                  Example: Economy flight 0.15 kg /km
                </p>
                <div className="mt-3">
                  <span className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                    Regional
                  </span>
                </div>
              </div>

              <div className="border rounded-md p-4 hover:shadow-md transition-shadow">
                <div className="font-bold mb-2">GHG Protocol</div>
                <p className="text-sm mb-2">Best for: Scope 1-3 frameworks</p>
                <p className="text-sm text-gray-600">
                  Example: Corporate supply chains
                </p>
                <div className="mt-3">
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    Global
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  handleSectionComplete(4);
                  setActiveSection(5);
                }}
                className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
              >
                Continue to Calculation Limits
              </button>
            </div>
          </div>
        )}

        {activeSection === 5 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Known Limitations
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
              <div className="lg:col-span-2">
                <p className="text-gray-700 mb-4">
                  While carbon footprint calculations are essential tools, they
                  come with important limitations that users should understand.
                  These include average vs. specific values, supply chain
                  visibility challenges, and the difference between emissions
                  and actual impact.
                </p>

                <table className="min-w-full mb-6">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-3 text-left">Limitation</th>
                      <th className="p-3 text-left">Why it happens</th>
                      <th className="p-3 text-left">
                        How we mitigate in real life
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="p-3 font-medium">Average ≠ Specific</td>
                      <td className="p-3">
                        Factor may assume EU car, not your SUV
                      </td>
                      <td className="p-3">
                        Choose "custom vehicle" in calculator
                      </td>
                    </tr>
                    <tr className="border-b">
                      <td className="p-3 font-medium">Scope 3 fog</td>
                      <td className="p-3">Supply-chain data missing</td>
                      <td className="p-3">Use ranges & disclose uncertainty</td>
                    </tr>
                    <tr>
                      <td className="p-3 font-medium">Emission ≠ Impact</td>
                      <td className="p-3">
                        Same CO₂e today ≠ tomorrow's warming cost
                      </td>
                      <td className="p-3">
                        Complement with social cost of carbon metrics
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-red-50 p-4 rounded-lg flex items-center justify-center">
                <div className="text-center">
                  <X className="h-12 w-12 mx-auto text-red-500 mb-4" />
                  <h3 className="text-lg font-medium text-red-800 mb-1">
                    Common Mistakes
                  </h3>
                  <ul className="text-left text-red-700 space-y-2">
                    <li>• Using outdated emission factors</li>
                    <li>• Double-counting overlapping activities</li>
                    <li>• Overlooking indirect emissions</li>
                    <li>• Assuming global averages apply locally</li>
                    <li>• Ignoring uncertainty ranges</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="bg-primary-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Mini-Quiz: Myth Busting
              </h3>

              <div className="space-y-4" id="myths-quiz">
                <MythQuestion
                  id="myth1"
                  statement="Electric cars are always zero-carbon."
                  isTrue={false}
                  explanation="Electric cars have zero tailpipe emissions, but their manufacturing and charging can have significant carbon footprints depending on the electricity grid mix."
                />

                <MythQuestion
                  id="myth2"
                  statement="A local apple always has a lower footprint than an imported one."
                  isTrue={false}
                  explanation="Transportation is just one factor. Growing methods, storage, and seasonal factors can make some imports less carbon-intensive than local out-of-season produce."
                />

                <MythQuestion
                  id="myth3"
                  statement="Digital activities have no carbon footprint."
                  isTrue={false}
                  explanation="Data centers, networks, and devices all consume electricity. Video streaming, for example, can generate significant emissions."
                />
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={() => {
                    handleSectionComplete(5);
                    setActiveSection(6);
                  }}
                  className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
                >
                  Continue to Why Counting Matters
                </button>
              </div>
            </div>
          </div>
        )}

        {activeSection === 6 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-bold mb-4">
              3.6 Why Counting Still Matters
            </h2>

            <div className="bg-primary-100 p-4 rounded-md mb-6 text-center">
              <p className="text-xl font-bold text-primary-800">
                "What gets measured gets managed."
              </p>
            </div>

            <h3 className="font-bold mb-4">
              Flip-card gallery (shocking analogies)
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-white border rounded-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative h-48 bg-primary-50 cursor-pointer group">
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="text-center">
                      <p className="font-bold mb-2">Burger</p>
                      <p>One beef burger = 3.0 kg CO₂e</p>
                      <p className="text-gray-600 text-sm mt-2">
                        That's the same as driving 12 km!
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-green-600 text-white p-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="text-center">
                      <p className="font-bold mb-2">Burger → Beans</p>
                      <p>Swap once a week →</p>
                      <p className="font-bold">save 0.8 t CO₂e/yr</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border rounded-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative h-48 bg-primary-50 cursor-pointer group">
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="text-center">
                      <p className="font-bold mb-2">Fast-fashion tee</p>
                      <p>One cotton T-shirt = 7 kg CO₂e</p>
                      <p className="text-gray-600 text-sm mt-2">
                        Like charging your phone for 2.5 years!
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-blue-600 text-white p-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="text-center">
                      <p className="font-bold mb-2">Fast-fashion → Thrift</p>
                      <p>Extend life by 9 months →</p>
                      <p className="font-bold">–30% item footprint</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white border rounded-md overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative h-48 bg-primary-50 cursor-pointer group">
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="text-center">
                      <p className="font-bold mb-2">Streaming binge</p>
                      <p>4K streaming for 1 hour = 0.42 kg CO₂e</p>
                      <p className="text-gray-600 text-sm mt-2">
                        About 2 km of driving!
                      </p>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-purple-600 text-white p-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="text-center">
                      <p className="font-bold mb-2">Streaming → Download</p>
                      <p>Download once →</p>
                      <p className="font-bold">cuts data-centre hits by 50%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => {
                  handleSectionComplete(6);
                  setActiveSection(7);
                }}
                className="px-4 py-2 rounded-md bg-[#349383] text-white hover:bg-primary-700"
              >
                Continue to Personal Footprint
              </button>
            </div>
          </div>
        )}

        {activeSection === 7 && (
          <div className="bg-white shadow-md rounded-lg p-6">
            <h2 className="text-2xl font-semibold text-gray-800 mb-4">
              Your One-Day Carbon Footprint
            </h2>

            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h3 className="font-bold mb-4 text-center">
                Track your personal carbon impact
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium mb-2 flex items-center">
                      <Car className="h-4 w-4 mr-2 text-primary-600" />
                      Transportation
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        className="p-2 border rounded"
                        onChange={(e) =>
                          handleFootprintCalculation(
                            "transport",
                            e.target.value,
                            personalFootprint.transport.amount || 10
                          )
                        }
                      >
                        <option value="">Select transport mode</option>
                        <option value="car">Car (petrol)</option>
                        <option value="bus">Bus</option>
                        <option value="train">Train</option>
                        <option value="bike">Bike/Walking</option>
                      </select>

                      <div className="flex">
                        <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md">
                          km
                        </span>
                        <input
                          type="number"
                          className="p-2 border rounded-r-md w-full"
                          placeholder="10"
                          value={personalFootprint.transport.amount || ""}
                          onChange={(e) =>
                            handleFootprintCalculation(
                              "transport",
                              personalFootprint.transport.item,
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2 flex items-center">
                      <Thermometer className="h-4 w-4 mr-2 text-primary-600" />
                      Home Energy
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        className="p-2 border rounded"
                        onChange={(e) =>
                          handleFootprintCalculation(
                            "energy",
                            e.target.value,
                            personalFootprint.energy.amount || 5
                          )
                        }
                      >
                        <option value="">Select energy type</option>
                        <option value="electricity">Electricity</option>
                        <option value="gas">Natural gas</option>
                      </select>

                      <div className="flex">
                        <span className="inline-flex items-center px-3 text-gray-500 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md">
                          kWh
                        </span>
                        <input
                          type="number"
                          className="p-2 border rounded-r-md w-full"
                          placeholder="5"
                          value={personalFootprint.energy.amount || ""}
                          onChange={(e) =>
                            handleFootprintCalculation(
                              "energy",
                              personalFootprint.energy.item,
                              e.target.value
                            )
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-5 border">
                  <h4 className="font-medium mb-4 text-center">
                    Daily Carbon Footprint Results
                  </h4>

                  <div className="space-y-3 mb-4">
                    {personalFootprint.transport.item && (
                      <div className="flex justify-between border-b pb-2">
                        <span>
                          Transportation (
                          {personalFootprint.transport.amount || 0} km{" "}
                          {personalFootprint.transport.item})
                        </span>
                        <span className="font-medium">
                          {personalFootprint.transport.footprint.toFixed(2)} kg
                          CO₂e
                        </span>
                      </div>
                    )}

                    {personalFootprint.energy.item && (
                      <div className="flex justify-between border-b pb-2">
                        <span>
                          Energy ({personalFootprint.energy.amount || 0} kWh{" "}
                          {personalFootprint.energy.item})
                        </span>
                        <span className="font-medium">
                          {personalFootprint.energy.footprint.toFixed(2)} kg
                          CO₂e
                        </span>
                      </div>
                    )}

                    {personalFootprint.breakfast.item && (
                      <div className="flex justify-between border-b pb-2">
                        <span>Food (breakfast items)</span>
                        <span className="font-medium">
                          {(
                            personalFootprint.breakfast.footprint +
                            personalFootprint.other.footprint
                          ).toFixed(2)}{" "}
                          kg CO₂e
                        </span>
                      </div>
                    )}

                    <div className="flex justify-between pt-2 font-bold">
                      <span>Total Daily Footprint</span>
                      <span>
                        {(
                          personalFootprint.transport.footprint +
                          personalFootprint.energy.footprint +
                          personalFootprint.breakfast.footprint +
                          personalFootprint.other.footprint
                        ).toFixed(2)}{" "}
                        kg CO₂e
                      </span>
                    </div>
                  </div>

                  {/* Comparison */}
                  {(personalFootprint.transport.footprint > 0 ||
                    personalFootprint.energy.footprint > 0) && (
                    <div className="mt-4 bg-primary-50 p-3 rounded text-sm">
                      <p className="font-medium mb-1">Comparison:</p>
                      <p>
                        The average daily footprint in UAE is ~18 kg CO₂e per
                        person.
                      </p>
                      <div className="mt-2 bg-gray-200 h-4 rounded-full overflow-hidden">
                        <div
                          className="bg-primary-600 h-full"
                          style={{
                            width: `${Math.min(
                              100,
                              ((personalFootprint.transport.footprint +
                                personalFootprint.energy.footprint +
                                personalFootprint.breakfast.footprint +
                                personalFootprint.other.footprint) /
                                18) *
                                100
                            )}%`,
                          }}
                        ></div>
                      </div>
                      <p className="text-right text-xs mt-1">
                        Your footprint:{" "}
                        {Math.round(
                          ((personalFootprint.transport.footprint +
                            personalFootprint.energy.footprint +
                            personalFootprint.breakfast.footprint +
                            personalFootprint.other.footprint) /
                            18) *
                            100
                        )}
                        % of average
                      </p>
                    </div>
                  )}

                  <div className="mt-6">
                    {resultsSaved ? (
                      <div className="flex flex-col items-center space-y-3">
                        <div className="bg-green-100 text-green-800 px-4 py-2 rounded-md flex items-center w-full">
                          <Check className="h-5 w-5 mr-2 text-green-600" />
                          <span>Results saved! Module completed.</span>
                        </div>
                        <p className="text-sm text-gray-600">
                          You can now proceed to the next module.
                        </p>
                      </div>
                    ) : (
                      <button
                        className="w-full bg-[#349383] text-white px-4 py-2 rounded-md font-medium hover:bg-primary-700"
                        onClick={handleModuleComplete}
                      >
                        Save Results & Complete Module
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Module Navigation */}
      <ModuleNavigation
        prevLink="/climate-engine"
        prevText="Previous: Climate Engine"
        nextLink="/uae-carbon-day"
        nextText="Next: UAE Carbon Day"
        onMarkComplete={() =>
          setCompletedSections({ ...completedSections, 7: true })
        }
        isCompleted={Object.keys(completedSections).length >= 7}
        completeButtonId="module-complete-button"
      />
    </div>
  );
};

// Helper component for the myths quiz
const MythQuestion = ({ id, statement, isTrue, explanation }) => {
  const [answered, setAnswered] = useState(false);
  const [userAnswer, setUserAnswer] = useState(null);

  return (
    <div className="p-4 border rounded-md bg-white">
      <p className="mb-3 font-medium">{statement}</p>

      {!answered ? (
        <div className="flex space-x-4">
          <button
            onClick={() => {
              setAnswered(true);
              setUserAnswer(false);
            }}
            className="px-4 py-2 bg-gray-200 hover:bg-red-100 text-gray-800 rounded transition-colors"
          >
            False
          </button>
          <button
            onClick={() => {
              setAnswered(true);
              setUserAnswer(true);
            }}
            className="px-4 py-2 bg-gray-200 hover:bg-green-100 text-gray-800 rounded transition-colors"
          >
            True
          </button>
        </div>
      ) : (
        <div>
          <div
            className={`p-3 rounded-md ${
              userAnswer === isTrue ? "bg-green-100" : "bg-red-100"
            } mb-3`}
          >
            {userAnswer === isTrue ? (
              <div className="flex">
                <Check className="h-5 w-5 text-green-600 mr-2" />
                <p className="font-medium">
                  {isTrue ? "Correct! It's true." : "Correct! It's false."}
                </p>
              </div>
            ) : (
              <div className="flex">
                <X className="h-5 w-5 text-red-600 mr-2" />
                <p className="font-medium">
                  Incorrect. It's actually {isTrue ? "true" : "false"}.
                </p>
              </div>
            )}
          </div>
          <p className="text-gray-700 text-sm">{explanation}</p>
        </div>
      )}
    </div>
  );
};

export default CarbonEmissions101;
